import { LOCALE_EVENTS, localeEvents, resetForNewSession } from '@/app/i18n';
import { queryClient } from '@/app/query-client';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { portalSessionQuery } from '@/services/api/endpoints/customer_download';
import { attractLoop } from '@/services/attract/attract-loop';
import { authConfig } from '@/services/auth/auth-config';
import { clientEvents } from '@/services/telemetry/client-events';
import { kioskJourney } from '@/services/telemetry/kiosk-journey';
import { sentryService } from '@/services/telemetry/sentry';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import sleep from '@/shared/utils/sleep';
import { track, warnMessageOf } from '@/shared/utils/track-telemetry';
import { analytics } from './Analytics';
import { faceDetection } from './FaceDetection';
import { kioskHardware } from './KioskHardware';
import { kioskStyle } from './KioskStyle';
import { payment } from './Payment';
import { photoProcessing } from './PhotoProcessing';
import { portraitGeneration } from './PortraitGeneration';

class Session extends PubSub {
  @reactive
  id = null;

  @reactive
  createdAt = null;

  @reactive
  gdprConsent = null;

  // GoPhoto integration - photopass code from QR scan
  @reactive
  gophotoCode = null;

  @reactive
  gophotoSendReadyOnly = false;

  EVENTS = {
    ENDED: 'ENDED',
  };

  LOCALSTORAGE_PREFIX = 'kiosk_sessionId';

  // Accumulates session update fields that failed to send.
  // On next successful update(), these are merged in so data is not permanently lost.
  _pendingUpdates = {};

  // Mutex flag to prevent concurrent create() calls from rapid touches
  _creating = false;

  // Mutex flag and promise to prevent concurrent end() calls.
  // On a 24/7 kiosk, end() can be triggered simultaneously by:
  //  - IdleWatcher.reset() (idle timeout)
  //  - create() (user starts new session before old one fully ended)
  //  - Direct calls from components (e.g., error recovery)
  // Without this guard, concurrent end() calls cause double analytics flush,
  // double Sentry session close, and double service resets.
  _isEnding = false;
  _endingPromise = null;

  constructor() {
    super();
    exposeToGlobalScope(this, 'Session');
  }

  get isCreating() {
    return this._creating;
  }

  /** Resolves when an end() in progress finished; at once without one. */
  async whenEnded() {
    try {
      await this._endingPromise;
    } catch {
      // The caller of end() handles its failure.
    }
  }

  async create(visitProperties = {}) {
    // Prevent concurrent session creation from rapid double-touches
    if (this._creating) {
      console.warn(
        '[Session] create() already in progress, ignoring duplicate call'
      );
      return;
    }
    this._creating = true;

    // If end() is already running (e.g., from IdleWatcher), wait for it to
    // complete before starting a new session. Without this, create() races
    // with the in-flight end() -- analytics flush, payment cancel, and
    // service resets from the old session interleave with new session setup.
    if (this._isEnding && this._endingPromise) {
      try {
        await this._endingPromise;
      } catch (e) {
        console.warn('[Session] Awaited in-progress end() failed:', e.message);
      }
    }

    try {
      if (this.id) {
        await this.end('abandoned', true);
      }
    } catch (e) {
      console.warn('[Session] end() failed during create():', e.message);
    }

    const analyticsVisitId = kioskJourney.begin({
      ...visitProperties,
      flow: store.kiosk?.gophoto_printing ? 'gophoto' : 'classic',
      kiosk_partner: store.kiosk?.partner ?? null,
    });

    return sentryService.withSpan(
      'session.create',
      'session.lifecycle',
      async () => {
        try {
          clientEvents.startTrace('session_create');
          clientEvents.record(
            'session_create_started',
            { source: store.kiosk?.slug },
            { area: 'session' }
          );

          // Prepare session data with source if available
          const sessionData = { ...attractLoop.sessionAttribution() };
          if (analyticsVisitId) {
            sessionData.analytics_visit_id = analyticsVisitId;
          }
          if (store.kiosk?.slug) {
            sessionData.source = store.kiosk.slug;
          }

          const session = await restApi.createSession(sessionData);
          this.id = session.session_id;
          kioskJourney.attachSession(session.session_id);
          this.createdAt = new Date();
          this.saveSessionIdLocally(session.session_id);
          clientEvents.attachSession(session.session_id);
          clientEvents.record(
            'session_created',
            {
              session_id: session.session_id,
              source: store.kiosk?.slug,
              kiosk_id: store.kioskId,
            },
            { area: 'session' }
          );

          // Set Sentry session context immediately after session creation
          track('[Session] Sentry context failed in create():', () => {
            sentryService.setSessionContext({
              sessionId: session.session_id,
              createdAt: this.createdAt,
              source: store.kiosk?.slug,
              kioskId: store.kioskId,
              deviceUuid: store.device?.device_uuid,
            });

            // CRITICAL: Start a new Sentry SDK session to prevent giant sessions
            // This is separate from setSessionContext - it controls Sentry's internal session tracking
            sentryService.startSentrySession();

            sentryService.addBreadcrumb('session', 'Session created', {
              session_id: session.session_id,
            });
          });

          // Track memory at session start
          sentryService.trackMemoryUsage('session_start');

          // Track session trial initiated (marks beginning of session funnel)
          analytics.trackTimeTo(analytics.METRICS.time_session_trial_initiated);

          // Track essentials and wait for it to complete
          try {
            await this.trackEssentials();
          } catch (e) {
            console.error('Failed to track essentials:', e);
            // Continue even if tracking fails - critical fields already set
          }

          return session;
        } catch (e) {
          sentryService.captureException(e, {
            tags: { operation: 'session_create' },
            extra: { kiosk_slug: store.kiosk?.slug },
          });
          kioskJourney.record('session_start_failed', {}, analyticsVisitId);
          kioskJourney.end('start_failed');
          useUiStore().showError('session_create_error', e);
          throw e;
        } finally {
          this._creating = false;
        }
      },
      { 'session.source': store.kiosk?.slug }
    );
  }

  async trackEssentials() {
    return sentryService.withSpan(
      'session.trackEssentials',
      'session.lifecycle',
      async () => {
        await sleep((resolve) =>
          kioskHardware.ever(kioskHardware.EVENTS.INITIALIZED, resolve)
        );
        await sleep((resolve) =>
          localeEvents.ever(LOCALE_EVENTS.INITIALIZED, resolve)
        );

        // Set Sentry kiosk and device context
        track('[Session] Sentry context failed in trackEssentials():', () => {
          sentryService.setKioskContext({
            slug: store.kiosk?.slug,
            apiKey: authConfig.getApiKey(),
            partner: store.kiosk?.partner,
            mode: store.kioskMode,
            isVertical: store.isVerticalKiosk,
            isMiniPC: store.isMiniPCKiosk,
            privacyVariant: store.kiosk?.privacy_policy_variant,
            gophotoEnabled: store.kiosk?.gophoto_printing,
          });

          sentryService.setDeviceContext({
            platform: store.device?.platform,
            browser: store.device?.browser,
            countryCode: store.device?.country_code,
            isMobile: store.isMobileDevice,
            viewportWidth: store.viewportWidth,
            viewportHeight: store.viewportHeight,
          });

          sentryService.setLocale(store.currentLocale?.code);
        });

        return this.update({
          source: store.kiosk?.slug,
          source_partner: store.kiosk?.partner,
          source_kiosk_id: store.kioskId,
          source_is_mini_pc: store.isMiniPCKiosk,

          session_engaged: true,
          language_initial: store.currentLocale?.code,
          price_per_portrait: store.kiosk?.price,
          price_currency: store.kiosk?.currency,

          remote_ip: store.device?.remote_ip,
          country_code: store.device?.country_code,
          device_uuid: store.device?.device_uuid,
          user_agent: store.device?.user_agent,
          user_agent_is_mobile: store.isMobileDevice,
          platform: store.device?.platform,
          browser: store.device?.browser,
          screen_width: store.device?.screen_width,
          screen_height: store.device?.screen_height,
        });
      },
      {
        'session.kiosk_slug': store.kiosk?.slug,
        'session.locale': store.currentLocale?.code,
      }
    );
  }

  async update(data) {
    // Merge any previously failed updates with the new data.
    // New data takes precedence over stale pending fields.
    const hasPending = Object.keys(this._pendingUpdates).length > 0;
    const mergedData = hasPending ? { ...this._pendingUpdates, ...data } : data;
    // Clear pending optimistically — re-added on failure below.
    if (hasPending) this._pendingUpdates = {};

    sentryService.addBreadcrumb('session', 'Session update', {
      session_id: this.id,
      fields_updated: Object.keys(mergedData || {}),
      ...(hasPending && { includes_pending: true }),
    });

    try {
      return await sentryService.withSpan(
        'session.update',
        'session.lifecycle',
        async () => {
          return restApi.updateSessionById(this.id, mergedData);
        },
        { 'session.fields': Object.keys(mergedData || {}).join(',') }
      );
    } catch (e) {
      // Save failed fields back to pending so the next update() includes them.
      // Merge rather than overwrite in case another update raced and also failed.
      this._pendingUpdates = { ...this._pendingUpdates, ...mergedData };
      throw e;
    }
  }

  async continue(sessionId, options = {}) {
    return this._continueWith(
      sessionId,
      () => restApi.getSessionById(sessionId),
      options
    );
  }

  /**
   * The customer portal has no kiosk api_key; its download code is the
   * credential, so it continues through the customer surface. The code form
   * just read the same session; its answer comes from the cache.
   */
  async continueFromDownloadCode(downloadCode, options = {}) {
    return this._continueWith(
      downloadCode,
      () => queryClient.fetchQuery(portalSessionQuery(downloadCode)),
      options
    );
  }

  async _continueWith(
    identifier,
    fetchSession,
    { captureError = true, showError = true } = {}
  ) {
    return sentryService.withSpan(
      'session.continue',
      'session.lifecycle',
      async () => {
        try {
          clientEvents.startTrace('session_continue');
          const session = await fetchSession();
          this.saveSessionIdLocally(session.session_id);
          this.id = session.session_id;
          kioskJourney.resume(session.session_id, {
            flow: store.kiosk?.gophoto_printing ? 'gophoto' : 'classic',
            kiosk_partner: store.kiosk?.partner ?? null,
          });
          this.createdAt = session.created_at
            ? new Date(session.created_at)
            : null;
          clientEvents.attachSession(session.session_id);
          clientEvents.record(
            'session_continue',
            {
              session_id: session.session_id,
              source: session.source || store.kiosk?.slug,
            },
            { area: 'session' }
          );
          this.gophotoCode = session.gophoto_code || null;
          // ?? keeps an explicit false (the user declined); || would lose it.
          this.gdprConsent = session.gdpr_consent ?? null;

          track('[Session] Sentry context failed in continue():', () => {
            sentryService.setSessionContext({
              sessionId: session.session_id,
              createdAt: this.createdAt,
              source: session.source || store.kiosk?.slug,
              kioskId: store.kioskId,
              deviceUuid: store.device?.device_uuid,
            });

            // A continued backend session is still a new browser instance to
            // Sentry, so it gets its own SDK session.
            sentryService.startSentrySession();

            sentryService.addBreadcrumb('session', 'Session continued', {
              session_id: session.session_id,
            });
          });

          return session;
        } catch (e) {
          if (captureError) {
            sentryService.captureException(e, {
              tags: { operation: 'session_continue' },
              extra: {
                // Full session ID is intentional Sentry metadata for DB joins.
                attempted_session_id: identifier,
              },
            });
          }
          if (showError) {
            useUiStore().showError('session_continue_error', e);
          }
          throw e;
        }
      },
      { 'session.continued_id': identifier }
    );
  }

  removeSavedSessionId() {
    localStorage.removeItem(this.LOCALSTORAGE_PREFIX);
  }

  saveSessionIdLocally(sessionId) {
    sessionId
      ? localStorage.setItem(this.LOCALSTORAGE_PREFIX, sessionId)
      : this.removeSavedSessionId();
  }

  /**
   * Record GDPR consent decision with optional timestamp.
   *
   * Under GDPR Article 7(1), the controller must be able to demonstrate
   * that the data subject consented AND when they did so.  The timestamp
   * is forwarded to the backend for audit trail purposes.
   *
   * @param {boolean} consented - Whether the user accepted or declined
   * @param {string} [timestamp] - ISO 8601 timestamp of when consent was given
   */
  async setGdprConsent(consented, timestamp) {
    this.gdprConsent = consented;

    sentryService.addBreadcrumb('session', 'GDPR consent set', {
      consented: consented,
      session_id: this.id,
      timestamp: timestamp || null,
    });
    sentryService.setTag('gdpr_consent', String(consented));

    if (this.id) {
      const updateData = { gdpr_consent: consented };
      if (timestamp) {
        updateData.gdpr_consent_at = timestamp;
      }
      try {
        await this.update(updateData);
      } catch (e) {
        // Update failed after retries — data saved to _pendingUpdates
        // by update() and will be included in the next successful update.
        console.warn(
          '[Session] GDPR consent update failed, queued for retry:',
          e.message
        );
      }
    }
  }

  /**
   * Set GoPhoto photopass code from QR scan
   * Called at the start of GoPhoto-enabled kiosk flow
   * @param {string} code - 6-character alphanumeric photopass code
   * @returns {Promise<boolean>} - Success status
   */
  async setGophotoCode(code) {
    if (!this.id) {
      console.error(
        '[Session] Cannot set GoPhoto code: session not initialized'
      );
      return false;
    }

    const normalizedCode = code.toUpperCase();

    return sentryService.withSpan(
      'session.setGophotoCode',
      'session.lifecycle',
      async () => {
        try {
          await restApi.setGophotoCode(normalizedCode);
          this.gophotoCode = normalizedCode;
          return true;
        } catch (e) {
          console.error('[Session] Failed to set GoPhoto code:', e);
          sentryService.captureException(e, {
            tags: { operation: 'gophoto_code' },
            extra: { code_length: code?.length },
            level: 'warning',
          });
          return false;
        }
      },
      { 'gophoto.code_length': normalizedCode?.length }
    );
  }

  async end(outcome = 'unknown', _calledFromCreate = false) {
    // 'completed' | 'abandoned' | 'idle_timeout' | 'error'
    // Guard: if create() is running, it already handles ending the old session
    // internally. Allowing EXTERNAL end() to run concurrently would race with
    // create()'s own end() call and could terminate the NEWLY created session
    // during the async gap between restApi.createSession() and this.id
    // assignment. On a 24/7 kiosk, this manifests as idle timeout killing a
    // session that the user just started.
    // _calledFromCreate bypasses this guard for create()'s own internal end() call.
    if (this._creating && !_calledFromCreate) {
      console.warn('[Session] end() skipped -- create() is in progress');
      return;
    }

    // Guard against concurrent end() calls.
    // On a 24/7 kiosk, end() can be triggered simultaneously by IdleWatcher,
    // create() (user starts new session), or error recovery. Without this,
    // concurrent calls cause double analytics flush, double Sentry session
    // close, and double service resets.
    if (this._isEnding) {
      // Return the in-flight promise so callers can await the same end()
      return this._endingPromise;
    }
    this._isEnding = true;
    this._endingPromise = this._doEnd(
      outcome,
      _calledFromCreate
        ? 'replaced'
        : outcome === 'abandoned'
          ? 'manual_reset'
          : outcome
    );
    try {
      return await this._endingPromise;
    } finally {
      this._isEnding = false;
      this._endingPromise = null;
    }
  }

  /** @private Internal end implementation. Callers use end(). */
  async _doEnd(outcome, visitEndReason = outcome) {
    // Set reset flag FIRST so reactive watchers in components can guard
    // against processing partial state during the multi-step reset.
    store.isResettingSession = true;

    // CRITICAL: Capture and clear session state FIRST (atomically)
    // This prevents race conditions where abort handlers try to use session.id
    // which would still be valid, triggering cascading errors
    const endingSessionId = this.id;
    const endingCreatedAt = this.createdAt;
    if (endingSessionId) kioskJourney.end(visitEndReason);
    clientEvents.record(
      'session_end_started',
      { session_id: endingSessionId, outcome },
      { area: 'session' }
    );

    // Clear session state BEFORE aborting to prevent abort handlers from using stale ID
    this.id = null;
    this.createdAt = null;
    this.gdprConsent = null;
    this.gophotoCode = null;
    this.gophotoSendReadyOnly = false;
    this._pendingUpdates = {};

    // If no session was active, exit early.
    // CRITICAL: Reset isResettingSession before returning -- without this, the
    // flag stays true permanently, causing all reactive watchers to skip
    // processing for the rest of the kiosk's lifetime.
    if (!endingSessionId) {
      console.warn('[Session] end() called but no active session');
      store.isResettingSession = false;
      return;
    }

    // Calculate duration using captured state
    const durationMs = endingCreatedAt
      ? Date.now() - endingCreatedAt.getTime()
      : null;

    // Flush pending analytics data for the ENDING session BEFORE clearing anything.
    // flushForSession() sends the queued metrics with the captured session ID, ensuring
    // events from session N never bleed into session N+1's identity.
    // This must happen before abortAllApiCalls() which would kill the flush request.
    try {
      await analytics.flushForSession(endingSessionId, 1500);
    } catch (e) {
      console.warn(
        '[Session] Analytics flush for ending session failed:',
        e.message
      );
    }

    // CRITICAL: Reset payment BEFORE aborting API calls.
    // resetForNewSession() sends a cancel-reader request that must go through.
    // If we abort first, the cancel request would be immediately killed,
    // potentially leaving a payment intent in requires_capture state (card charged but uncaptured).
    try {
      await payment.resetForNewSession();
    } catch (e) {
      console.error(
        '[Session] Payment reset failed during session end:',
        e.message
      );
    }

    // The captured id, because this.id is already null.
    restApi.sendGenerationCancel(endingSessionId);

    // NOW abort remaining API calls - abort handlers will see null session.id and bail gracefully
    restApi.abortAllApiCalls();

    // Add Sentry breadcrumb and end Sentry SDK session (using captured ID)
    track('[Session] Sentry context failed in end():', () => {
      sentryService.addBreadcrumb('session', 'Session ended', {
        session_id: endingSessionId,
        duration_ms: durationMs,
        outcome: outcome,
      });

      // Set session_outcome tag and counter BEFORE ending the Sentry session
      // so they're included in the final session capture. If set after
      // clearSessionContext() the tag is cleared immediately, leaving an
      // orphaned tag that pollutes the next user's Sentry events.
      sentryService.setTag('session_outcome', outcome);
      sentryService.incrementCounter(`session.outcome.${outcome}`, 1, {
        kiosk_slug: store.kiosk?.slug,
      });

      // Track session duration metrics BEFORE ending the Sentry session so they
      // are associated with the ending session's context (session_id, kiosk_slug).
      // If tracked after endSentrySession() + clearSessionContext(), the duration
      // metrics would have no session identity.
      if (durationMs !== null) {
        const durationMinutes = durationMs / 60000;
        sentryService.trackMetric(
          'session.duration_ms',
          durationMs,
          'millisecond',
          {
            kiosk_slug: store.kiosk?.slug,
          }
        );
        sentryService.trackMetric(
          'session.duration_minutes',
          durationMinutes,
          'none',
          {
            kiosk_slug: store.kiosk?.slug,
          }
        );
        sentryService.trackMemoryUsage('session_end');
      }

      // CRITICAL: End Sentry SDK session BEFORE clearing context
      // This ensures the final session capture includes all context data
      // Map outcome to Sentry session status
      const sentryStatus =
        outcome === 'error'
          ? 'crashed'
          : outcome === 'idle_timeout'
            ? 'abnormal'
            : 'exited';
      sentryService.endSentrySession(sentryStatus);

      // Now clear context after Sentry session is properly closed
      sentryService.clearSessionContext();
    });

    // Clear ALL accumulated Sentry contexts for session-scoped data.
    // On a 24/7 kiosk that never refreshes, stale data from user A
    // bleeds into user B's Sentry events if not explicitly cleared.
    // NOTE: Session-scoped TAGS (gdpr_consent, selected_style_id, session_outcome,
    // etc.) are already cleared by clearSessionContext() above -- it iterates the
    // full sessionTags list in Sentry.js. Only CONTEXTS need clearing here since
    // clearSessionContext() only handles the session identity context.
    sentryService.setContext('kiosk_config', null);
    sentryService.setContext('kiosk_styles', null);
    sentryService.setContext('selected_style', null);
    sentryService.setContext('art_styles', null);
    sentryService.setContext('face_selection', null);
    sentryService.setContext('detected_faces', null); // FaceDetection.js
    sentryService.setContext('download_gallery', null); // CustomerPortraitsDownloadCode.vue
    sentryService.setContext('download_attempt', null); // CustomerPortraitsDownload.vue
    sentryService.setContext('portrait_generation', null); // PortraitGeneration.js
    // Note: 'hardware' and 'device_info' contexts persist across sessions (device-level)
    // Note: 'network' context persists across sessions (device-level)
    // Note: 'kiosk' context persists across sessions (set once per kiosk config load)
    // Note: 'payment' and 'printer' contexts are cleared by their services

    // Trigger event with captured ID (session state already cleared above)
    this.trigger(this.EVENTS.ENDED, endingSessionId);

    // CRITICAL: Reset store.ui mutable fields BEFORE service resets to prevent
    // reactive watchers from seeing inconsistent state. Without this, a watcher
    // watching both store.portraits and store.ui.footerProgressIndicatorCurrentStep
    // could fire when portraits is cleared (by portraitGeneration.reset) but the
    // step counter is still at the old value -- causing a stale UI frame.
    const ui = useUiStore();
    ui.showLogoStartOverButton = false;
    ui.showUploadedPhotos = false;
    ui.footerProgressIndicatorCurrentStep = 0;
    ui.showPrivacyPolicyPopup = false;

    // Clear any lingering UI overlays (error popups, loading indicators) from the
    // ended session. Without this, an error popup from User A's session stays
    // visible when User B starts a new session.
    ui.overlayQueue = [];

    // Reset each service in a try/catch so a failure in one does not abort
    // the remaining resets. On a 24/7 kiosk, leaving ANY service in a stale
    // state permanently corrupts all subsequent sessions. Without per-service
    // try/catch, an uncaught throw from faceDetection.reset() (or any service
    // below it) skips the remaining resets AND leaves store.isResettingSession
    // = true permanently, breaking every reactive watcher.
    const serviceResets = [
      ['faceDetection', () => faceDetection.reset()],
      ['photoProcessing', () => photoProcessing.reset()],
      // printing.reset() is called internally by portraitGeneration.reset()
      ['portraitGeneration', () => portraitGeneration.reset()],
      // payment.resetForNewSession() already called above (before abortAllApiCalls)
      // Only the selection of the visitor: the styles belong to the kiosk
      // config, which KioskConfig changes only between sessions.
      ['kioskStyle', () => kioskStyle.resetForNewSession()],
      // Reset language to kiosk default so User A's choice doesn't persist for User B
      ['i18n', () => resetForNewSession()],
      ['analytics', () => analytics.resetForNewSession()],
    ];

    for (const [name, resetFn] of serviceResets) {
      try {
        resetFn();
      } catch (e) {
        console.error(
          `[Session] ${name}.reset() failed during session end:`,
          e
        );
        sentryService.captureException(e, {
          tags: { component: 'Session', operation: 'end_service_reset' },
          extra: { service: name, ending_session_id: endingSessionId },
        });
      }
    }

    this.removeSavedSessionId();

    // Session reset complete -- clear the flag so components stop guarding.
    // This MUST be at the very end, after all service resets and state cleanup.
    store.isResettingSession = false;
    clientEvents.record(
      'session_ended',
      {
        session_id: endingSessionId,
        outcome,
        duration_ms: durationMs,
      },
      { area: 'session' }
    );
    clientEvents.endTrace(outcome);

    // Flush Sentry events to ensure all session data is sent
    // Important: Don't await this in case browser is closing
    sentryService
      .flush(1000)
      .catch((e) => warnMessageOf('[Session] Sentry flush failed:', e));
  }
}

export const session = new Session();
